'use client';
import { useMemo, useRef } from 'react';
import { InView } from 'react-intersection-observer';
import { useQuery, useLazyQuery } from '@apollo/client';
import _ from 'lodash';

// GraphQL imports
import { gql } from 'shared/__generated__/gql';
import {
  GetFeedViewQuery,
  GetGenericFeedInfiniteScrollQuery,
  FeedItemType,
  ProfileThemeType,
  FullFeedItemFragment,
  FeedItemOrderBy,
  GetGenericFeedCarouselViewQueryVariables,
} from 'shared/__generated__/graphql';

// Components
import { Button } from 'web2/app/components/button';
import FeedItem from './feed-item';
import SkeletonFeedItem from 'shared/misc/components/SkeletonFeedItem';
import { useBreakpoint } from 'shared/misc/providers/breakpoint-provider';

// Hooks and utilities
import { useAuth } from 'shared/misc/hooks/useAuth';
import { isInScrollData } from '../../../utils/scrolling';
import { WindowVirtualizerHandle } from 'virtua';
import { UseGenericFeedToolbarProps, useGenericFeedToolbar } from './generic-feed-toolbar';
import FeedItemCarousel from 'shared/misc/components/FeedItemCarousel';
import { cn } from '../../../utils/cn';
import RecItemSlimInlineComposer from '../recommendations/rec-slim-inline-composer';
import UserTile from '../user/user-tile';

const FeedViewQuery = gql(/* GraphQL */ `
  query getGenericFeedInfiniteScroll(
    $first: Int
    $after: String
    $showGlobalFeed: Boolean
    $originUserId: String
    $isRandomSelection: Boolean
    $types: [FeedItemType!]
    $category: RecCategory
    $orderBy: FeedItemOrderBy
  ) {
    genericFeedInfiniteScroll: feedConnection(
      first: $first
      after: $after
      showGlobalFeed: $showGlobalFeed
      originUserId: $originUserId
      isRandomSelection: $isRandomSelection
      types: $types
      category: $category
      orderBy: $orderBy
    ) @connection(key: "genericFeedInfiniteScroll") {
      pageInfo {
        startCursor
        endCursor
        hasNextPage
      }
      edges {
        cursor
        node {
          ...FullFeedItem
        }
      }
    }
  }
`);

const GenericFeedCarouselViewQuery = gql(/* GraphQL */ `
  query getGenericFeedCarouselView(
    $first: Int
    $after: String
    $showGlobalFeed: Boolean
    $originUserId: String
    $isRandomSelection: Boolean
    $types: [FeedItemType!]
    $category: RecCategory
    $orderBy: FeedItemOrderBy
  ) {
    genericFeedCarousel: feedConnection(
      first: $first
      after: $after
      showGlobalFeed: $showGlobalFeed
      originUserId: $originUserId
      isRandomSelection: $isRandomSelection
      types: $types
      category: $category
      orderBy: $orderBy
    ) @connection(key: "genericFeedCarousel") {
      pageInfo {
        startCursor
        endCursor
        hasNextPage
      }
      edges {
        cursor
        node {
          ...FullFeedItem
        }
      }
    }
  }
`);

const ViewMoreFeedViewQuery = gql(/* GraphQL */ `
  query getViewMoreFeedView2(
    $before: String
    $showGlobalFeed: Boolean
    $types: [FeedItemType!]
    $category: RecCategory
  ) {
    newFeedItemCount(
      before: $before
      showGlobalFeed: $showGlobalFeed
      types: $types
      excludeSessionUser: true
      category: $category
    )
  }
`);

enum FeedContentFilter {
  ALL = 'ALL',
  ONLY_RECS = 'ONLY_RECS',
}

const PAGE_LENGTH = 15;

interface FeedItem {
  id: string;
  node: FullFeedItemFragment;
}

interface FeedEdge {
  cursor: string;
  node: FullFeedItemFragment;
}

interface Carousel {
  title: string;
  index?: number;
  variables: GetGenericFeedCarouselViewQueryVariables;
}

interface FeedInfiniteScrollProps extends UseGenericFeedToolbarProps {
  onlyFollowing?: boolean;
  isInView?: boolean;
  filter?: FeedContentFilter;
  mainFeedData?: GetFeedViewQuery;
  originUserId?: string;
  isRandomSelection?: boolean;
  enabledFeedItemTypes?: FeedItemType[];
  forceTypes?: FeedItemType[];
  includeOriginUsername?: boolean;
  orderBy?: FeedItemOrderBy;
  enabledOrderByOptions?: FeedItemOrderBy[];
  includeTopCarousel?: boolean;
  belowFiltersNode?: React.ReactNode;
  carouselVariables?: GetGenericFeedCarouselViewQueryVariables;
  carouselTitle?: string;
  feedTitle?: string;
  includeComposer?: boolean;
  carousels?: Carousel[];
  includeQuickFilters?: boolean;
}

export function FeedInfiniteScroll({
  onlyFollowing = true,
  isInView: isTopInView = true,
  filter = FeedContentFilter.ALL,
  mainFeedData: initialFeedData,
  originUserId,
  enabledFeedItemTypes,
  isRandomSelection,
  forceTypes,
  includeOriginUsername = false,
  orderBy,
  enabledOrderByOptions,
  belowFiltersNode,
  includeTopCarousel = false,
  carouselVariables,
  carouselTitle,
  feedTitle,
  carousels,
  includeComposer = false,
  includeQuickFilters = true,
  ...extraOptions
}: FeedInfiniteScrollProps) {
  const auth = useAuth();

  const {
    variables,
    isNonDefaultFilter,
    quickFilterNodes,
    isInline,
    sortByAsInlineMenuNode,
    filterNode,
    isNonDefaultSortBy,
  } = useGenericFeedToolbar({
    onlyFollowing,
    forceTypes,
    enabledFeedItemTypes,
    originUserId,
    isRandomSelection,
    orderBy,
    enabledOrderByOptions,
    ...extraOptions,
  });

  const {
    data: upToDateData,
    loading,
    networkStatus,
    error,
    refetch,
    fetchMore,
    variables: currentVariables,
  } = useQuery(FeedViewQuery, {
    fetchPolicy: isInScrollData() ? 'cache-first' : 'cache-and-network',
    variables: {
      ...variables,
      after: null,
      first: PAGE_LENGTH,
    },
    notifyOnNetworkStatusChange: true,
  });

  const breakpoint = useBreakpoint();
  const isMobile = ['sm', 'base'].includes(breakpoint);

  const data = upToDateData ? upToDateData : initialFeedData;

  type FeedQueryResult = GetGenericFeedInfiniteScrollQuery & { genericFeedInfiniteScroll: any };
  const connection = (data as FeedQueryResult)?.genericFeedInfiniteScroll;

  const getNextPage = async () => {
    if (!connection?.pageInfo.hasNextPage) return;
    await fetchMore({
      variables: {
        ...currentVariables,
        first: PAGE_LENGTH,
        after: connection?.pageInfo.endCursor,
      },
    });
  };

  const feedItems = (connection?.edges || []).map(({ node }: FeedEdge) => node);

  const virtuaRef = useRef<WindowVirtualizerHandle>(null);

  // Add this ref to track fetched items
  const fetchedCountRef = useRef(0);
  const uniqueFeedItems = useMemo(() => {
    const ids = new Set<string>();
    return feedItems.filter((item: FeedItem) => {
      if (!item) return false;
      if (ids.has(item.id)) return false;
      ids.add(item.id);
      return true;
    });
  }, [feedItems]);

  const carouselsByIndex = useMemo(() => {
    return (
      carousels?.reduce((acc, carousel) => {
        if (!carousel.index) return acc;
        acc[carousel.index] = carousel;
        return acc;
      }, {} as Record<number, Carousel>) || {}
    );
  }, [carousels]);

  return (
    <div className="flex flex-col items-center justify-center gap-[24px] w-[800px] max-w-full pb-[250px]">
      <div className="flex flex-col gap-6 flex-1 w-full">
        {includeComposer && auth.user && (
          <div className={cn(!auth.user ? 'flex flex-col justify-center' : '', 'w-full')}>
            <div className="w-full flex justify-center">
              <div className="min-w-full">
                <RecItemSlimInlineComposer withInvertedEmojiLink startCollapsed redirectToRec />
              </div>
            </div>
          </div>
        )}
        {includeTopCarousel && !isMobile && (
          <div className="w-full flex justify-center mt-[12px]">
            <InFeedCarousel
              carousel={{
                title: carouselTitle ?? 'Top of the day',
                variables: carouselVariables ?? {
                  types: [FeedItemType.NewPrompt],
                  orderBy: FeedItemOrderBy.TopOfDay,
                },
              }}
            />
          </div>
        )}
        <div className="w-full flex justify-between gap-4">
          <div className="flex gap-4">{!isMobile ? sortByAsInlineMenuNode : quickFilterNodes}</div>
          <div className="flex gap-4">
            {includeQuickFilters && !isMobile && quickFilterNodes}
            {filterNode}
          </div>
        </div>
        <div className="">
          {(!isNonDefaultFilter || !isNonDefaultSortBy) && belowFiltersNode}
          {feedTitle ? (
            <div className="w-full">
              <div className="typography-heading-lg">{feedTitle}</div>
            </div>
          ) : null}
          {uniqueFeedItems.map((feedItem: FullFeedItemFragment, idx: number) => {
            const feedItemNode = (
              <div className="w-full flex justify-center py-4" key={feedItem.id}>
                <FeedItem
                  feedItem={feedItem}
                  hideAllFeedContext={true}
                  hideContextForTypes={
                    filter === FeedContentFilter.ONLY_RECS
                      ? new Set([FeedItemType.AddRecommendation, FeedItemType.NewPromptRecReply])
                      : undefined
                  }
                  promptProps={{ isInline }}
                  recProps={{ isInline }}
                  includeOriginUsername={includeOriginUsername}
                />
              </div>
            );

            const carouselConfig =
              (!isNonDefaultFilter || !isNonDefaultSortBy) && !isMobile && carouselsByIndex[idx];

            return (
              <>
                {feedItemNode}
                {carouselConfig && (
                  <div className="w-full flex justify-center my-[24px]">
                    <InFeedCarousel carousel={carouselConfig} />
                  </div>
                )}
              </>
            );
          })}
          {!!uniqueFeedItems.length && (
            <InView
              rootMargin="750px 0px"
              onChange={(inView) => {
                if (connection?.pageInfo.hasNextPage && inView) {
                  fetchedCountRef.current = feedItems.length;
                  getNextPage();
                }
              }}
            />
          )}

          {/* <WindowVirtualizer
            ref={virtuaRef}
            className="h-full w-full"
            itemSize={200}
            overscan={5}
            onScroll={async (e) => {
              if (!virtuaRef.current) return;

              if (
                connection?.pageInfo.hasNextPage &&
                virtuaRef.current.findEndIndex() + 5 > feedItems.length
              ) {
                fetchedCountRef.current = feedItems.length;
                await getNextPage();
              }
            }}
          >
            {networkStatus === NetworkStatus.fetchMore && (
              <div className="flex justify-center py-4">
                <Spinner size="lg" />
              </div>
            )}

            {uniqueFeedItems.length === 0 && !loading && (
              <div className="flex justify-center py-4">
                <Text textStyle="brand.headingSm">Nothing to see here.</Text>
              </div>
            )}
          </WindowVirtualizer> */}
        </div>
        {loading && !uniqueFeedItems.length && (
          <>
            {[...Array(20)].map((_, index) => (
              <SkeletonFeedItem key={index} />
            ))}
          </>
        )}
        {!loading && !uniqueFeedItems.length && (
          <div className="flex bg-brand-background justify-center w-full">
            <p className="text-brand-main typography-heading-sm p-[12px] border border-dashed border-brand-main rounded-md w-full">
              No results. Try adjusting the filters.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function InFeedCarousel({ carousel }: { carousel: Carousel }) {
  const { data: carouselData, loading: carouselLoading } = useQuery(GenericFeedCarouselViewQuery, {
    fetchPolicy: 'cache-first',
    variables: {
      after: null,
      first: 10,
      ...carousel.variables,
    },
    notifyOnNetworkStatusChange: true,
  });

  const breakpoint = useBreakpoint();
  const isMobile = ['sm', 'base'].includes(breakpoint);

  let { width, height } = useMemo(() => {
    const firstNode = carouselData?.genericFeedCarousel?.edges[0]?.node;

    switch (firstNode?.type) {
      case FeedItemType.NewUser:
        return { width: isMobile ? undefined : 200, height: 400 };
      default:
        return { width: isMobile ? undefined : 600, height: 300 };
    }
  }, [carouselData?.genericFeedCarousel?.edges, isMobile]);

  return (
    <FeedItemCarousel
      height={{ base: undefined, sm: height }}
      withStars={false}
      itemWidth={width}
      containerProps={{
        p: { base: '0px', sm: '0px' },
        borderWidth: '0px',
      }}
      titleNode={<div className="typography-heading-lg text-brand-highlight">{carousel.title}</div>}
      lastNode={
        <div
          className="absolute right-0 bottom-0 w-[50px] bg-gradient-to-l from-brand-background to-transparent pointer-events-none"
          style={{ height: height - 50 }}
        />
      }
    >
      {!carouselData?.genericFeedCarousel && carouselLoading
        ? [...Array(10)].map((_, index) => <SkeletonFeedItem key={index} skeletons={1} />)
        : carouselData?.genericFeedCarousel?.edges.map((edge) => {
            return <InlineFeedCarouselItem node={edge.node} />;
          })}
    </FeedItemCarousel>
  );
}

function InlineFeedCarouselItem({ node }: { node: FullFeedItemFragment }) {
  const breakpoint = useBreakpoint();
  const isMobile = ['sm', 'base'].includes(breakpoint);

  switch (node.type) {
    case FeedItemType.NewUser:
      return <UserTile user={node.originUser} />;
    default:
      return (
        <FeedItem
          feedItem={node}
          recProps={
            !isMobile
              ? {
                  disableExpandImage: true,
                  isInline: true,
                  height: '200px',
                }
              : {
                  disableExpandImage: true,
                  noOfLines: 2,
                  // height: '500px',
                }
          }
          promptProps={
            !isMobile
              ? {
                  isInline: true,
                  height: '200px',
                  verticallyCentered: false,
                }
              : {
                  noOfLines: 2,
                  // height: '400px',
                }
          }
        />
      );
  }
}
